import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

import { refusalText } from "../../../api/errors";
import { useApiCommand, useApiQuery } from "../../../api/hooks";
import type { OpenPointView } from "../../../api/types";
import { useCurrentMe } from "../../../app/auth";
import { Button, Card, Dialog, EmptyState, Loading, Pill } from "../../../ui";
import { ElementSearchPicker } from "../components/ElementPicker";
import { FormError, RadioGroup, TextArea, TextInput } from "../components/fields";
import { ElementLink } from "../components/panels";
import { useCommandId, useRefresh } from "../hooks";
import { deliverablePath } from "../lib/paths";
import { whenWords } from "../lib/text";

type Filter = "open" | "closed" | "all";

/** What an open point is about: the ids its scope names. */
export function scopeIds(scope: unknown): { element: string[]; deliverable: string[] } {
  const record = scope && typeof scope === "object" && !Array.isArray(scope) ? (scope as Record<string, unknown>) : {};
  const text = (value: unknown) => (typeof value === "string" && value ? value : null);
  return {
    element: [text(record.element_id), text(record.item_id)].filter((x): x is string => x !== null),
    deliverable: [text(record.deliverable_id)].filter((x): x is string => x !== null),
  };
}

/** The screen at /open-points: questions that are still open, and a place to log a new one. */
export function OpenPointsPage() {
  const me = useCurrentMe();
  const editable = me.active?.roles.includes("engineer") ?? false;
  const [filter, setFilter] = useState<Filter>("open");
  const points = useApiQuery("open_points", { open: filter === "all" ? null : filter === "open" });

  return (
    <div className="stack item-screen">
      <Card aria-labelledby="open-title">
        <h1 id="open-title">Open points</h1>
        <p>Questions that are still open: something your team has to answer, a conflict found in a document, or a comment about loaded content. They are repeated in the hand-over.</p>
        <RadioGroup
          legend="Show"
          value={filter}
          options={[
            { value: "open", label: "Open" },
            { value: "closed", label: "Closed" },
            { value: "all", label: "All" },
          ]}
          onChange={(value) => setFilter(value as Filter)}
        />
      </Card>

      {points.isPending ? (
        <Loading>Reading the open points</Loading>
      ) : points.isError ? (
        <Card>
          <EmptyState title="The open points could not be read">{refusalText(points.error)}</EmptyState>
        </Card>
      ) : points.data.length === 0 ? (
        <Card>
          <EmptyState title={filter === "closed" ? "No closed open point" : "Nothing is open"}>{filter === "open" ? "There is no open question in this discipline." : "Nothing matches this filter."}</EmptyState>
        </Card>
      ) : (
        <Card aria-label="The open points">
          <ul className="open-points">
            {points.data.map((point) => (
              <PointRow key={point.id} point={point} editable={editable} />
            ))}
          </ul>
        </Card>
      )}

      {editable && <LogCard knownKinds={[...new Set((points.data ?? []).map((p) => p.kind))]} />}
    </div>
  );
}

function PointRow({ point, editable }: { point: OpenPointView; editable: boolean }) {
  const [closing, setClosing] = useState(false);
  const reopen = useApiCommand("reopen_open_point");
  const refresh = useRefresh();
  const about = scopeIds(point.scope);
  return (
    <li className="open-point" data-closed={point.closed ? "true" : undefined}>
      <p className="open-point__text">{point.text}</p>
      <p className="muted">
        <Pill>{point.kind.replace(/_/g, " ")}</Pill> Raised by {point.raised_by}, {whenWords(point.at)}.
      </p>
      {(about.element.length > 0 || about.deliverable.length > 0) && (
        <p className="muted">
          About:{" "}
          {about.element.map((id) => (
            <span key={id}>
              <ElementLink id={id} />{" "}
            </span>
          ))}
          {about.deliverable.map((id) => (
            <Link key={id} to={deliverablePath(id)}>
              the document
            </Link>
          ))}
        </p>
      )}
      {point.closed && (
        <p className="muted">
          Closed by {point.closed.by}, {whenWords(point.closed.at)}: {point.closed.reason}
        </p>
      )}
      {editable && (
        <div className="row">
          {point.closed ? (
            <Button
              variant="secondary"
              busy={reopen.isPending}
              onClick={() => reopen.mutate({ id: point.id }, { onSuccess: () => refresh("open_points", "back_lane") })}
            >
              Open it again
            </Button>
          ) : (
            <Button variant="secondary" onClick={() => setClosing(true)}>
              Close it
            </Button>
          )}
        </div>
      )}
      <FormError message={reopen.error ? refusalText(reopen.error) : null} />
      {closing && <CloseDialog point={point} onClose={() => setClosing(false)} />}
    </li>
  );
}

function CloseDialog({ point, onClose }: { point: OpenPointView; onClose: () => void }) {
  const [reason, setReason] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const close = useApiCommand("close_open_point");
  const refresh = useRefresh();
  const submit = () => {
    if (!reason.trim()) {
      setProblem("Say how it was answered.");
      return;
    }
    close.mutate(
      { id: point.id, reason: reason.trim() },
      {
        onSuccess: () => {
          refresh("open_points", "back_lane");
          onClose();
        },
      },
    );
  };
  return (
    <Dialog
      closeOnBackdrop={false}
      open
      onClose={onClose}
      title="Close this open point"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={close.isPending} onClick={submit}>
            Close it
          </Button>
        </>
      }
    >
      <p>{point.text}</p>
      <TextArea label="How was it answered?" required rows={2} value={reason} error={problem} onChange={setReason} />
      <FormError message={close.error ? refusalText(close.error) : null} />
    </Dialog>
  );
}

function LogCard({ knownKinds }: { knownKinds: string[] }) {
  const [text, setText] = useState("");
  const [kind, setKind] = useState("question");
  const [about, setAbout] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const log = useApiCommand("log_open_point");
  const ids = useCommandId();
  const refresh = useRefresh();

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const found: Record<string, string> = {};
    if (!text.trim()) found.text = "Write the question.";
    if (!kind.trim()) found.kind = "Say what kind of open point it is.";
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    log.mutate(
      { text: text.trim(), kind: kind.trim().toLowerCase().replace(/\s+/g, "_"), scope: about ? { element_id: about } : {}, command_id: ids.current() },
      {
        onSuccess: () => {
          ids.renew();
          setText("");
          setAbout("");
          refresh("open_points", "back_lane");
        },
      },
    );
  };

  const kinds = [...new Set(["question", ...knownKinds])];
  return (
    <Card aria-labelledby="log-title">
      <h2 id="log-title">Log an open point</h2>
      <form onSubmit={submit} noValidate className="stack" aria-label="Log an open point">
        <TextArea label="The question or the problem" required rows={3} value={text} error={errors.text} onChange={setText} />
        <TextInput label="Kind" required hint="A word for the sort of open point." value={kind} error={errors.kind} list="open-point-kinds" onChange={setKind} />
        <datalist id="open-point-kinds">
          {kinds.map((k) => (
            <option key={k} value={k} />
          ))}
        </datalist>
        <ElementSearchPicker label="About an element (optional)" value={about} onChange={setAbout} />
        <div className="row">
          <Button type="submit" variant="primary" busy={log.isPending}>
            Log it
          </Button>
        </div>
        <FormError message={log.error ? refusalText(log.error) : null} />
      </form>
    </Card>
  );
}
